import { useState, useRef, useEffect, type ReactNode, type DragEvent } from "react";
import {
  Sparkles,
  Droplets,
  PartyPopper,
  GraduationCap,
  Sofa,
  ShoppingBag,
  Package,
  Users,
  Home,
  UtensilsCrossed,
  Briefcase,
  LayoutGrid,
  ArrowLeft,
  ArrowRight,
  Plus,
  X,
  ImagePlus,
  Video,
  Trash2,
  MessageCircle,
  Instagram,
  Phone,
  Check,
  Tag,
  AlertCircle,
} from "lucide-react";
import type { DemoFormData, IletisimKanali, Campaign } from "../demoTypes";
import {
  SORU_PRESETS,
  MAX_OZEL_SORU,
  MAX_VIDEO,
  MAX_GUCLU_YON,
  GUCLU_YON_OPTIONS,
  MIN_SERVICE_CATEGORY,
  MAX_SERVICE_CATEGORY,
  MIN_CATEGORY_IMAGE,
  MAX_CATEGORY_IMAGE,
  MAX_CAMPAIGN,
  defaultDemoFormData,
} from "../demoTypes";
import {
  SECTOR_CONFIGS,
  findSectorConfig,
  getServiceOptions,
  getItemLabel,
  OTHER_SECTOR_ID,
  CTA_POOL,
  CTA_RULES,
  type SectorConfig,
  type SectorCta,
  type CtaDestination,
} from "../sectorConfigs";
import { trackDemoEvent } from "../smartDemoTracking";

const STEPS = [
  { no: 1, id: "company", label: "İşletme", short: "İşletme" },
  { no: 2, id: "services", label: "Hizmetler", short: "Hizmetler" },
  { no: 3, id: "media", label: "Medya", short: "Medya" },
  { no: 4, id: "contact", label: "İletişim", short: "İletişim" },
  { no: 5, id: "flow", label: "Müşteri Akışı", short: "Akış" },
];

const SECTOR_ICONS: Record<string, typeof Sparkles> = {
  beauty: Sparkles,
  cleaning: Droplets,
  event: PartyPopper,
  education: GraduationCap,
  realestate: Home,
  food: UtensilsCrossed,
  furniture: Sofa,
  network: Users,
  onlinesales: ShoppingBag,
  localproducts: Package,
  professional: Briefcase,
  other: LayoutGrid,
};

let uid = 0;
const nextId = (prefix: string) => `${prefix}-${++uid}`;

const inputClass =
  "w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15";
const labelClass = "mb-2 block text-sm font-semibold text-foreground-800";

export default function DemoWizard({
  onComplete,
  onExit,
}: {
  onComplete: (data: DemoFormData) => void;
  onExit: () => void;
}) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<DemoFormData>({ ...defaultDemoFormData });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  const update = (patch: Partial<DemoFormData>) =>
    setData((d) => ({ ...d, ...patch }));

  const clearError = (key: string) =>
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });

  const validateStep = (): boolean => {
    const next: Record<string, string> = {};
    if (step === 1) {
      if (!data.firmaAdi.trim()) next.firmaAdi = "Firma adınızı yazın";
      if (!data.sektor) next.sektor = "Bir sektör seçin";
      if (findSectorConfig(data.sektor)?.id === OTHER_SECTOR_ID && !data.sektorDiger.trim())
        next.sektorDiger = "Sektörünüzü yazın";
    }
    if (step === 2) {
      if (data.serviceCategories.length < MIN_SERVICE_CATEGORY) {
        next.serviceCategories = "En az bir hizmet / kategori ekleyin";
      } else {
        data.serviceCategories.forEach((cat) => {
          if (cat.images.length < MIN_CATEGORY_IMAGE) {
            next[`cat-${cat.id}`] = "Bu hizmet için en az 3 görsel yükleyin";
          }
        });
        data.campaigns.forEach((cmp) => {
          if (!cmp.name.trim()) next[`cmp-name-${cmp.id}`] = "Kampanya adını yazın";
          if (!cmp.newPrice.trim()) next[`cmp-price-${cmp.id}`] = "Yeni fiyatı yazın";
        });
      }
    }
    if (step === 4) {
      if (!data.whatsapp.trim()) next.whatsapp = "WhatsApp numaranızı girin";
      if (data.selectedCtas.some((c) => c.destination === "website") && !data.website.trim()) {
        next.ctaWebsite =
          "Web sitesi CTA'sı seçili. Web sitesi adresinizi girin veya bu CTA'yı kaldırın.";
      }
      if (data.selectedCtas.some((c) => c.destination === "instagram") && !data.instagram.trim()) {
        next.ctaInstagram =
          "Instagram CTA'sı seçili. Instagram kullanıcı adınızı girin veya bu CTA'yı kaldırın.";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const scrollToFirstError = () => {
    window.setTimeout(() => {
      const el = contentRef.current?.querySelector("[data-field-error]");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }, 60);
  };

  const next = () => {
    if (!validateStep()) {
      scrollToFirstError();
      return;
    }
    const currentStepId = STEPS[step - 1]?.id;
    if (currentStepId) {
      trackDemoEvent(`form:step:completed:${currentStepId}`, "form_step_completed", {
        step: currentStepId,
      });
      if (currentStepId === "company") {
        trackDemoEvent("form:company:completed", "company_info_completed");
      }
      if (currentStepId === "services") {
        trackDemoEvent("form:services:completed", "service_categories_completed");
      }
    }
    if (step === 5) {
      const categoryImageCount = data.serviceCategories.reduce(
        (sum, c) => sum + c.images.length,
        0,
      );
      const imageCount = categoryImageCount + data.gorseller.length + (data.logo ? 1 : 0);
      trackDemoEvent("form:completed", "form_completed", {
        service_count: data.serviceCategories.length,
        image_count: imageCount,
        video_count: data.videolar.length,
        campaign_count: data.campaigns.length,
      });
      onComplete(data);
    } else {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const back = () => {
    if (step === 1) onExit();
    else setStep((s) => s - 1);
  };

  /* Form tracking — açılış bir kez, step görünürlüğü her step değişiminde. */
  useEffect(() => {
    trackDemoEvent("form:started", "form_started");
  }, []);

  useEffect(() => {
    const sid = STEPS[step - 1]?.id;
    if (!sid) return;
    trackDemoEvent(`form:step:viewed:${sid}`, "form_step_viewed", { step: sid });
    if (sid === "media") {
      trackDemoEvent("form:media:reached", "media_step_reached", { step: sid });
    }
  }, [step]);

  const errorCount = Object.keys(errors).length;
  const sectorConfig = findSectorConfig(data.sektor);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto max-w-3xl px-4 pb-16 pt-6 md:px-6 md:pt-10">
        {/* Progress header */}
        <div className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-heading text-sm font-bold text-primary-700">SMART DEMO</span>
            <span className="text-xs font-semibold text-foreground-500">{step} / 5</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-300">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-500"
              style={{ width: `${step * 20}%` }}
            />
          </div>
          {/* Step labels */}
          <div className="mt-4 flex items-center justify-between">
            {STEPS.map((s) => {
              const active = s.no === step;
              const done = s.no < step;
              return (
                <div key={s.no} className="flex flex-1 items-center">
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition ${
                        done
                          ? "bg-primary-500 text-background-50"
                          : active
                            ? "bg-primary-600 text-background-50"
                            : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      {done ? <Check className="h-4 w-4" strokeWidth={2.5} /> : s.no}
                    </span>
                    <span
                      className={`mt-1.5 hidden whitespace-nowrap text-[11px] font-semibold sm:block ${
                        active ? "text-foreground-900" : "text-foreground-400"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {s.no < 5 && (
                    <div
                      className={`mx-1 mb-5 h-px flex-1 sm:mb-6 ${
                        s.no < step ? "bg-primary-500" : "bg-background-300"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div
          key={step}
          ref={contentRef}
          className="animate-fade-in-up rounded-2xl border border-background-200 bg-background-50 p-5 md:p-8"
        >
          {step === 1 && <Step1 data={data} update={update} clearError={clearError} errors={errors} />}
          {step === 2 && (
            <ServiceStep2 data={data} update={update} clearError={clearError} errors={errors} config={sectorConfig} />
          )}
          {step === 3 && <Step3 data={data} update={update} config={sectorConfig} />}
          {step === 4 && <Step4 data={data} update={update} clearError={clearError} errors={errors} />}
          {step === 5 && <Step5 data={data} update={update} />}
        </div>

        {/* Footer nav */}
        <div className="mt-6">
          {errorCount > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-accent-300 bg-accent-100 px-4 py-3 text-sm font-semibold text-accent-800">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Devam etmek için eksik alanları tamamlayın.
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={back}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-700 transition hover:bg-background-200"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </button>
            <button
              onClick={next}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              {step === 5 ? "Demomu Oluştur" : "Devam Et"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Paylaşılan küçük bileşenler ---------- */

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p
      data-field-error
      className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-accent-100 px-3 py-2 text-xs font-semibold text-accent-800"
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      {message}
    </p>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-accent-600">*</span>}
      </label>
      {children}
      <FieldError message={error} />
    </div>
  );
}

function destinationLabel(d: CtaDestination): string {
  switch (d) {
    case "whatsapp":
      return "WhatsApp";
    case "website":
      return "Web sitesi";
    case "instagram":
      return "Instagram";
    case "phone":
      return "Telefon";
    default:
      return d;
  }
}

/* ---------- STEP 1 ---------- */

function Step1({
  data,
  update,
  clearError,
  errors,
}: {
  data: DemoFormData;
  update: (p: Partial<DemoFormData>) => void;
  clearError: (k: string) => void;
  errors: Record<string, string>;
}) {
  const [digerYon, setDigerYon] = useState("");
  const [digerOpen, setDigerOpen] = useState(false);

  const selectSector = (sektor: string) => {
    if (data.sektor === sektor) {
      clearError("sektor");
      return;
    }
    // Structured medya blob URL'lerini güvenli revoke et.
    data.serviceCategories.forEach((cat) => {
      cat.images.forEach((u) => {
        if (u.startsWith("blob:")) URL.revokeObjectURL(u);
      });
    });
    data.videolar.forEach((u) => {
      if (u.startsWith("blob:")) URL.revokeObjectURL(u);
    });
    data.gorseller.forEach((u) => {
      if (u.startsWith("blob:")) URL.revokeObjectURL(u);
    });
    const cfg = findSectorConfig(sektor);
    update({
      sektor,
      hizmetler: [],
      anaHizmet: "",
      kampanyaMetni: "",
      gorseller: [],
      videolar: [],
      serviceCategories: [],
      campaigns: [],
      selectedCtas: cfg ? [...cfg.defaultCtas] : [],
    });
    clearError("sektor");
  };

  const toggleYon = (yon: string) => {
    const has = data.gucluYonler.includes(yon);
    if (has) update({ gucluYonler: data.gucluYonler.filter((x) => x !== yon) });
    else if (data.gucluYonler.length < MAX_GUCLU_YON)
      update({ gucluYonler: [...data.gucluYonler, yon] });
  };

  const addDigerYon = () => {
    const val = digerYon.trim();
    if (!val) return;
    if (data.gucluYonler.length >= MAX_GUCLU_YON) return;
    update({ gucluYonler: [...data.gucluYonler, val] });
    setDigerYon("");
    setDigerOpen(false);
  };

  return (
    <div className="space-y-6">
      <StepTitle title="Önce sizi tanıyalım" subtitle="İşletmenizi birkaç bilgiyle tanıyın." />
      <Field label="Firma Adı" required error={errors.firmaAdi}>
        <input
          className={inputClass}
          placeholder="Örn. Lalissa Beauty"
          value={data.firmaAdi}
          onChange={(e) => {
            update({ firmaAdi: e.target.value });
            clearError("firmaAdi");
          }}
        />
      </Field>
      <Field label="Yetkili Adı">
        <input
          className={inputClass}
          placeholder="Adınız Soyadınız"
          value={data.yetkiliAdi}
          onChange={(e) => update({ yetkiliAdi: e.target.value })}
        />
      </Field>
      <Field label="Faaliyet Alanı / Sektör" required error={errors.sektor}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {SECTOR_CONFIGS.map((cfg) => {
            const Icon = SECTOR_ICONS[cfg.id] || LayoutGrid;
            const selected = data.sektor === cfg.label;
            return (
              <button
                key={cfg.id}
                type="button"
                onClick={() => selectSector(cfg.label)}
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border px-3 py-4 text-center transition ${
                  selected
                    ? "border-primary-500 bg-primary-50 text-primary-800"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50/50"
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={1.8} />
                <span className="text-xs font-semibold leading-tight">{cfg.label}</span>
              </button>
            );
          })}
        </div>
        {findSectorConfig(data.sektor)?.id === OTHER_SECTOR_ID && (
          <div className="mt-3">
            <input
              className={inputClass}
              placeholder="Sektörünüzü yazın"
              value={data.sektorDiger}
              onChange={(e) => {
                update({ sektorDiger: e.target.value });
                clearError("sektorDiger");
              }}
            />
            <FieldError message={errors.sektorDiger} />
          </div>
        )}
      </Field>
      <Field label="Hizmet Bölgesi">
        <input
          className={inputClass}
          placeholder="Örn. Eryaman, Etimesgut, Ankara"
          value={data.hizmetBolgesi}
          onChange={(e) => update({ hizmetBolgesi: e.target.value })}
        />
      </Field>
      <Field label="Kaç yıldır hizmet veriyorsunuz?">
        <input
          type="number"
          min={0}
          max={99}
          className={inputClass}
          placeholder="Örn. 8"
          value={data.tecrubeYili ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") update({ tecrubeYili: null });
            else {
              const n = Math.floor(Number(v));
              if (!Number.isNaN(n) && n >= 0) update({ tecrubeYili: Math.min(n, 99) });
            }
          }}
        />
      </Field>
      <Field label="İşletmenizin en güçlü yönleri (en fazla 3)">
        <div className="flex flex-wrap gap-2">
          {GUCLU_YON_OPTIONS.map((yon) => {
            if (yon === "Diğer") {
              return (
                <button
                  key={yon}
                  type="button"
                  onClick={() => setDigerOpen((o) => !o)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                    digerOpen
                      ? "border-primary-500 bg-primary-50 text-primary-800"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  + Diğer
                </button>
              );
            }
            const selected = data.gucluYonler.includes(yon);
            return (
              <button
                key={yon}
                type="button"
                onClick={() => toggleYon(yon)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                  selected
                    ? "border-primary-500 bg-primary-50 text-primary-800"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                {yon}
              </button>
            );
          })}
        </div>
        {digerOpen && (
          <div className="mt-3 flex gap-2">
            <input
              className={inputClass}
              placeholder="Diğer güçlü yönünüzü yazın"
              value={digerYon}
              onChange={(e) => setDigerYon(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDigerYon();
                }
              }}
            />
            <button
              type="button"
              onClick={addDigerYon}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              <Plus className="h-4 w-4" />
              Ekle
            </button>
          </div>
        )}
        <p className="mt-1.5 text-xs text-foreground-400">
          {data.gucluYonler.length} / {MAX_GUCLU_YON} seçildi
        </p>
      </Field>
      <Field label="Güven / tanıtım notu">
        <textarea
          rows={3}
          className={inputClass}
          placeholder="Örn. 7 yıldır Eryaman'da hizmet veriyoruz."
          value={data.guvenNotu}
          onChange={(e) => update({ guvenNotu: e.target.value })}
        />
      </Field>
    </div>
  );
}

/* ---------- STEP 2 — Ortak hizmet / kategori + kampanya ---------- */

function ServiceStep2({
  data,
  update,
  clearError,
  errors,
  config,
}: {
  data: DemoFormData;
  update: (p: Partial<DemoFormData>) => void;
  clearError: (k: string) => void;
  errors: Record<string, string>;
  config?: SectorConfig;
}) {
  const [kategoriInput, setKategoriInput] = useState("");
  const suggestions = config ? getServiceOptions(config) : [];
  const itemLabel = config ? getItemLabel(config) : "hizmetler";

  const addKategori = (ad: string) => {
    const val = ad.trim();
    if (!val) return;
    if (data.serviceCategories.length >= MAX_SERVICE_CATEGORY) return;
    if (data.serviceCategories.some((c) => c.name.toLowerCase() === val.toLowerCase())) return;
    update({
      serviceCategories: [
        ...data.serviceCategories,
        { id: nextId("cat"), name: val, images: [] },
      ],
    });
    setKategoriInput("");
    clearError("serviceCategories");
  };

  const removeKategori = (id: string) => {
    const cat = data.serviceCategories.find((c) => c.id === id);
    if (cat) {
      cat.images.forEach((u) => {
        if (u.startsWith("blob:")) URL.revokeObjectURL(u);
      });
    }
    update({
      serviceCategories: data.serviceCategories.filter((c) => c.id !== id),
    });
  };

  const setKategoriImages = (id: string, images: string[]) => {
    update({
      serviceCategories: data.serviceCategories.map((c) =>
        c.id === id ? { ...c, images } : c,
      ),
    });
    clearError(`cat-${id}`);
  };

  const addCampaign = () => {
    if (data.campaigns.length >= MAX_CAMPAIGN) return;
    update({
      campaigns: [
        ...data.campaigns,
        { id: nextId("cmp"), name: "", oldPrice: "", newPrice: "", description: "" },
      ],
    });
  };

  const updateCampaign = (id: string, patch: Partial<Campaign>) => {
    update({
      campaigns: data.campaigns.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
    clearError(`cmp-name-${id}`);
    clearError(`cmp-price-${id}`);
  };

  const removeCampaign = (id: string) => {
    update({ campaigns: data.campaigns.filter((c) => c.id !== id) });
  };

  return (
    <div className="space-y-8">
      <StepTitle
        title="Demonuzda hangi hizmetleri öne çıkarmak istersiniz?"
        subtitle="En güçlü 3–5 hizmet veya ürün kategorinizi seçmeniz daha etkili bir demo oluşturur."
      />

      {/* Önerilen hizmetler */}
      <div>
        <span className={labelClass}>Önerilen {itemLabel}</span>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((ad) => {
            const added = data.serviceCategories.some((c) => c.name === ad);
            return (
              <button
                key={ad}
                type="button"
                onClick={() => addKategori(ad)}
                disabled={added || data.serviceCategories.length >= MAX_SERVICE_CATEGORY}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                  added
                    ? "border-primary-500 bg-primary-50 text-primary-800"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
                }`}
              >
                {added && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                {ad}
              </button>
            );
          })}
        </div>
      </div>

      {/* Özel hizmet ekleme */}
      <div>
        <div className="flex gap-2">
          <input
            className={inputClass}
            placeholder={config?.customServiceLabel ?? "Farklı bir hizmet adı yazın"}
            value={kategoriInput}
            onChange={(e) => setKategoriInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addKategori(kategoriInput);
              }
            }}
          />
          <button
            type="button"
            onClick={() => addKategori(kategoriInput)}
            disabled={data.serviceCategories.length >= MAX_SERVICE_CATEGORY}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
            Ekle
          </button>
        </div>
        <p className="mt-1.5 text-xs text-foreground-400">
          {data.serviceCategories.length} / {MAX_SERVICE_CATEGORY} seçildi
        </p>
        <FieldError message={errors.serviceCategories} />
      </div>

      {/* Kategori kartları */}
      {data.serviceCategories.length > 0 && (
        <div className="space-y-5">
          {data.serviceCategories.map((cat) => (
            <div key={cat.id} className="rounded-2xl border border-background-200 bg-background-100/40 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-bold text-foreground-900">{cat.name}</span>
                  <p className="text-xs text-foreground-400">
                    Bu hizmete ait en az {MIN_CATEGORY_IMAGE} görsel yükleyin
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeKategori(cat.id)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition hover:bg-accent-100 hover:text-accent-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <MediaDropzone
                label={`${cat.name} Görselleri`}
                hint={`En az ${MIN_CATEGORY_IMAGE} - en fazla ${MAX_CATEGORY_IMAGE} görsel`}
                accept="image/*"
                isVideo={false}
                multiple
                max={MAX_CATEGORY_IMAGE}
                files={cat.images}
                onFiles={(files) => setKategoriImages(cat.id, files)}
              />
              <FieldError message={errors[`cat-${cat.id}`]} />
            </div>
          ))}
        </div>
      )}

      {/* Kampanyalar */}
      {config?.supportsCampaigns !== false && (
        <div className="border-t border-background-200 pt-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-heading text-base font-bold text-foreground-950">Güncel Kampanyalar</h3>
              <p className="mt-0.5 text-sm text-foreground-500">
                Kampanya eklemek zorunlu değildir; en fazla {MAX_CAMPAIGN} kampanya ekleyebilirsiniz.
              </p>
            </div>
            <button
              type="button"
              onClick={addCampaign}
              disabled={data.campaigns.length >= MAX_CAMPAIGN}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
              Kampanya Ekle
            </button>
          </div>

          {data.campaigns.length > 0 && (
            <div className="space-y-4">
              {data.campaigns.map((cmp, i) => (
                <CampaignCard
                  key={cmp.id}
                  index={i}
                  kampanya={cmp}
                  errors={errors}
                  onChange={(patch) => updateCampaign(cmp.id, patch)}
                  onRemove={() => removeCampaign(cmp.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CampaignCard({
  index,
  kampanya,
  errors,
  onChange,
  onRemove,
}: {
  index: number;
  kampanya: Campaign;
  errors: Record<string, string>;
  onChange: (patch: Partial<Campaign>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-2xl border border-background-200 bg-background-100/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-bold text-foreground-900">
          <Tag className="h-4 w-4 text-primary-600" strokeWidth={2} />
          Kampanya {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition hover:bg-accent-100 hover:text-accent-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        <Field label="Kampanya / Hizmet Adı" required error={errors[`cmp-name-${kampanya.id}`]}>
          <input
            className={inputClass}
            placeholder="Örn. Microblading"
            value={kampanya.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Eski Fiyat">
            <input
              className={inputClass}
              placeholder="Örn. 3000 TL"
              value={kampanya.oldPrice ?? ""}
              onChange={(e) => onChange({ oldPrice: e.target.value })}
            />
          </Field>
          <Field label="Yeni Fiyat / Kampanya Fiyatı" required error={errors[`cmp-price-${kampanya.id}`]}>
            <input
              className={inputClass}
              placeholder="Örn. 1500 TL"
              value={kampanya.newPrice}
              onChange={(e) => onChange({ newPrice: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Kısa Açıklama">
          <textarea
            rows={2}
            className={inputClass}
            placeholder="Örn. Eylül ayına özel sınırlı kontenjan."
            value={kampanya.description ?? ""}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Field>
      </div>
    </div>
  );
}

/* ---------- STEP 3 ---------- */

function Step3({
  data,
  update,
  config,
}: {
  data: DemoFormData;
  update: (p: Partial<DemoFormData>) => void;
  config?: SectorConfig;
}) {
  return (
    <div className="space-y-7">
      <StepTitle
        title="İşletmenizi müşterilerinize gösterelim"
        subtitle="Logo ve işlem / sonuç videolarınızı ekleyin."
      />

      <MediaDropzone
        label="Logo"
        hint="1 görsel"
        accept="image/*"
        isVideo={false}
        multiple={false}
        max={1}
        files={data.logo ? [data.logo] : []}
        onFiles={(files) => update({ logo: files[0] || null })}
      />

      {config?.supportsVideos !== false && (
        <>
          <p className="-mt-4 text-sm text-foreground-500">
            Video yüklemek zorunlu değildir; ancak 1–2 kısa işlem, ürün, hizmet veya sonuç videosu
            eklemek demonuzu çok daha etkileyici hale getirir.
          </p>

          <MediaDropzone
            label="İşlem / Sonuç Videoları"
            hint={`En fazla ${MAX_VIDEO} video`}
            accept="video/*"
            isVideo
            multiple
            max={MAX_VIDEO}
            files={data.videolar}
            onFiles={(files) => update({ videolar: files })}
          />
        </>
      )}
    </div>
  );
}

function MediaDropzone({
  label,
  hint,
  accept,
  isVideo,
  multiple,
  max,
  files,
  onFiles,
}: {
  label: string;
  hint: string;
  accept: string;
  isVideo: boolean;
  multiple: boolean;
  max: number;
  files: string[];
  onFiles: (files: string[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const urls = Array.from(fileList)
      .filter((f) => (isVideo ? f.type.startsWith("video/") : f.type.startsWith("image/")))
      .map((f) => URL.createObjectURL(f));
    if (urls.length === 0) return;
    onFiles([...files, ...urls].slice(0, max));
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const remove = (idx: number) => {
    const removed = files[idx];
    if (removed && removed.startsWith("blob:")) URL.revokeObjectURL(removed);
    onFiles(files.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-semibold text-foreground-800">{label}</span>
        <span className="text-xs text-foreground-400">{hint}</span>
      </div>

      {files.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {files.map((url, idx) => (
            <div
              key={url}
              className="group relative aspect-square overflow-hidden rounded-xl border border-background-200 bg-background-100"
            >
              {isVideo ? (
                <video
                  src={url}
                  controls
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
              ) : (
                <img src={url} alt={label} className="h-full w-full object-cover object-top" />
              )}
              <button
                type="button"
                onClick={() => remove(idx)}
                className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-foreground-950/70 text-background-50 transition hover:bg-accent-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}

          {files.length < max && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-background-300 text-foreground-400 transition hover:border-primary-400 hover:text-primary-500"
            >
              <Plus className="h-5 w-5" />
              <span className="text-xs font-medium">Ekle</span>
            </button>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-background-300 bg-background-50 px-4 py-10 text-center transition hover:border-primary-400 hover:bg-primary-50/40"
        >
          {isVideo ? (
            <Video className="h-8 w-8 text-foreground-400" strokeWidth={1.5} />
          ) : (
            <ImagePlus className="h-8 w-8 text-foreground-400" strokeWidth={1.5} />
          )}
          <div>
            <p className="text-sm font-semibold text-foreground-700">
              Sürükleyip bırakın veya tıklayın
            </p>
            <p className="mt-1 text-xs text-foreground-400">{hint}</p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

/* ---------- STEP 4 ---------- */

function Step4({
  data,
  update,
  clearError,
  errors,
}: {
  data: DemoFormData;
  update: (p: Partial<DemoFormData>) => void;
  clearError: (k: string) => void;
  errors: Record<string, string>;
}) {
  const [showCtaPool, setShowCtaPool] = useState(false);

  const kanallar: { key: IletisimKanali; label: string; icon: typeof MessageCircle }[] = [
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { key: "instagram", label: "Instagram DM", icon: Instagram },
    { key: "telefon", label: "Telefon", icon: Phone },
  ];

  const addCta = (cta: SectorCta) => {
    if (data.selectedCtas.length >= CTA_RULES.max) return;
    if (data.selectedCtas.some((c) => c.id === cta.id)) return;
    update({ selectedCtas: [...data.selectedCtas, cta] });
    setShowCtaPool(false);
  };

  const removeCta = (id: string) => {
    update({ selectedCtas: data.selectedCtas.filter((c) => c.id !== id) });
    clearError("ctaWebsite");
    clearError("ctaInstagram");
  };

  const availableCtas = CTA_POOL.filter(
    (c) => !data.selectedCtas.some((s) => s.id === c.id),
  );

  return (
    <div className="space-y-6">
      <StepTitle
        title="Müşterileriniz size nasıl ulaşacak?"
        subtitle="İletişim bilgilerinizi ve müşterilerinize sunacağınız aksiyonları belirleyin."
      />

      {/* CTA seçimi */}
      <div className="rounded-2xl border border-background-200 bg-background-100/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <span className="text-sm font-bold text-foreground-900">Müşteri Aksiyonları</span>
            <p className="text-xs text-foreground-400">
              {data.selectedCtas.length} / {CTA_RULES.max} aksiyon
            </p>
          </div>
        </div>

        {data.selectedCtas.length > 0 && (
          <div className="space-y-2">
            {data.selectedCtas.map((cta) => (
              <div
                key={cta.id}
                className="flex items-center justify-between gap-2 rounded-xl border border-background-200 bg-background-50 px-3 py-2.5"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <div className="min-w-0 leading-tight">
                    <span className="block truncate text-sm font-semibold text-foreground-800">
                      {cta.label}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {destinationLabel(cta.destination)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeCta(cta.id)}
                  className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition hover:bg-accent-100 hover:text-accent-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {availableCtas.length > 0 && data.selectedCtas.length < CTA_RULES.max && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowCtaPool((o) => !o)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200"
            >
              <Plus className="h-4 w-4" />
              CTA Ekle
            </button>
            {showCtaPool && (
              <div className="mt-2 flex flex-wrap gap-2">
                {availableCtas.map((cta) => (
                  <button
                    key={cta.id}
                    type="button"
                    onClick={() => addCta(cta)}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:bg-primary-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {cta.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {(errors.ctaWebsite || errors.ctaInstagram) && (
          <FieldError message={errors.ctaWebsite || errors.ctaInstagram} />
        )}
      </div>

      <Field label="WhatsApp Numarası" required error={errors.whatsapp}>
        <input
          className={inputClass}
          placeholder="05xx xxx xx xx"
          inputMode="tel"
          value={data.whatsapp}
          onChange={(e) => {
            update({ whatsapp: e.target.value });
            clearError("whatsapp");
          }}
        />
      </Field>

      <Field label="Instagram" error={errors.ctaInstagram}>
        <input
          className={inputClass}
          placeholder="@firmaadi"
          value={data.instagram}
          onChange={(e) => {
            update({ instagram: e.target.value });
            clearError("ctaInstagram");
          }}
        />
      </Field>

      <Field label="Facebook">
        <input
          className={inputClass}
          placeholder="facebook.com/firmaadi"
          value={data.facebook}
          onChange={(e) => update({ facebook: e.target.value })}
        />
      </Field>

      <Field label="Web Sitesi" error={errors.ctaWebsite}>
        <input
          className={inputClass}
          placeholder="www.firmaadi.com"
          value={data.website}
          onChange={(e) => {
            update({ website: e.target.value });
            clearError("ctaWebsite");
          }}
        />
      </Field>

      <Field label="Müşterinin yönleneceği ana kanal">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {kanallar.map((k) => {
            const Icon = k.icon;
            const selected = data.anaIletisimKanali === k.key;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => update({ anaIletisimKanali: k.key })}
                className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-4 py-3.5 text-left transition ${
                  selected
                    ? "border-primary-500 bg-primary-50"
                    : "border-background-300 bg-background-50 hover:border-primary-300"
                }`}
              >
                <Icon className="h-5 w-5 shrink-0 text-primary-600" strokeWidth={1.8} />
                <span className="text-sm font-semibold text-foreground-800">{k.label}</span>
                {selected && <Check className="ml-auto h-4 w-4 text-primary-600" />}
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

/* ---------- STEP 5 ---------- */

function Step5({
  data,
  update,
}: {
  data: DemoFormData;
  update: (p: Partial<DemoFormData>) => void;
}) {
  const [ozelSoruInput, setOzelSoruInput] = useState("");

  const toggleSoru = (s: string) => {
    const has = data.sorular.includes(s);
    update({
      sorular: has ? data.sorular.filter((x) => x !== s) : [...data.sorular, s],
    });
  };

  const addOzelSoru = () => {
    const val = ozelSoruInput.trim();
    if (!val || data.ozelSorular.length >= MAX_OZEL_SORU) return;
    update({ ozelSorular: [...data.ozelSorular, val] });
    setOzelSoruInput("");
  };

  return (
    <div className="space-y-6">
      <StepTitle title="Müşterilerinizi daha iyi tanıyalım" subtitle="Reklamdan gelen bir kişiden hangi bilgileri almak istersiniz?" />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SORU_PRESETS.map((s) => {
          const selected = data.sorular.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleSoru(s)}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                selected
                  ? "border-primary-500 bg-primary-50"
                  : "border-background-300 bg-background-50 hover:border-primary-300"
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition ${
                  selected ? "border-primary-500 bg-primary-500" : "border-background-400"
                }`}
              >
                {selected && <Check className="h-3.5 w-3.5 text-background-50" strokeWidth={3} />}
              </span>
              <span className="text-sm font-medium text-foreground-800">{s}</span>
            </button>
          );
        })}
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-sm font-semibold text-foreground-800">Özel Soru Ekle</span>
          <span className="text-xs text-foreground-400">
            {data.ozelSorular.length} / {MAX_OZEL_SORU}
          </span>
        </div>

        {data.ozelSorular.length > 0 && (
          <div className="mb-3 space-y-2">
            {data.ozelSorular.map((q, i) => (
              <div
                key={`${q}-${i}`}
                className="flex items-center justify-between gap-2 rounded-xl bg-background-100 px-4 py-2.5"
              >
                <span className="text-sm text-foreground-800">{q}</span>
                <button
                  type="button"
                  onClick={() => update({ ozelSorular: data.ozelSorular.filter((_, x) => x !== i) })}
                  className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center text-foreground-400 transition hover:text-accent-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            className={inputClass}
            placeholder="Kaç kişilik bir davet planlıyorsunuz?"
            value={ozelSoruInput}
            onChange={(e) => setOzelSoruInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOzelSoru();
              }
            }}
          />
          <button
            type="button"
            onClick={addOzelSoru}
            disabled={data.ozelSorular.length >= MAX_OZEL_SORU}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
            Ekle
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Başlık ---------- */

function StepTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{title}</h2>
      {subtitle && <p className="mt-1.5 text-sm text-foreground-500">{subtitle}</p>}
    </div>
  );
}